import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { CompanyService, ComplianceService } from '$/services';
import { toastError } from '$/store/toast/actions';
import Loading from '$/components/loading/Loading';
import Modal from '$/components/global/modal';
import * as Bixby from '$/core';
import '../index.scss';
import { AgGridReact } from 'ag-grid-react';
import { ClientCompanyDeclarationStatus, UserCompanyDeclarationStatus } from '$/core/enums';
import { getHasBixbyComplianceAutoApprovalFeatureFlagState } from '$/store/login/selectors';
import { AppState } from '$/store';
import dayjs from 'dayjs';
import BixButton from '$/components/global/bixButton';
import { Link } from 'react-router-dom';
import { DeclarationPortfolio } from '$/core/models';
import { orderBy } from '$/core/utils';

function DeclarationStatusString({
  declarationStatus,
}: {
  declarationStatus: Bixby.Enums.ClientCompanyDeclarationStatus;
}) {
  switch (declarationStatus) {
    case Bixby.Enums.ClientCompanyDeclarationStatus.Pending:
      return <span>Pending Your Approval</span>;
    case Bixby.Enums.ClientCompanyDeclarationStatus.ClientApproved:
      return <span>Pending Bixby Approval</span>;
    case Bixby.Enums.ClientCompanyDeclarationStatus.Rejected:
      return <span>Rejected</span>;
    case Bixby.Enums.ClientCompanyDeclarationStatus.BixbyApproved:
      return <span>Approved</span>;
    case Bixby.Enums.ClientCompanyDeclarationStatus.Deleted:
      return <span>Deleted</span>;
    case Bixby.Enums.ClientCompanyDeclarationStatus.None:
      return <span>None</span>;
    default:
      return <span>{declarationStatus}</span>;
  }
}

interface ICompanyAccessProps {
  hasBixbyComplianceAutoApprovalFeatureFlag: boolean;
  dispatchToastError: (message: string) => Promise<void>;
}

interface IUserCompanyDeclarationView {
  id: string;
  declarationStatus: UserCompanyDeclarationStatus;
  requestedBy_Email: string;
  requestedDate: Date;
  companyId: string;
  companyName: string;
  companyDeclarationStatus: ClientCompanyDeclarationStatus;
  updatedDate: Date;
}

interface IHistoricalDeclarationView {
  id: string;
  clientDecisionBy_Email: string;
  clientDecisionDate: Date;
  declarationStatus: string;
  requestedBy_Email: string;
  requestedDate: Date;
  companyName: string;
  companyId: string;
  type: string;
  updatedDate: Date;
}

const CompanyAccess: React.FC<ICompanyAccessProps> = props => {
  const { hasBixbyComplianceAutoApprovalFeatureFlag, dispatchToastError } = props;

  const [, setCompanyDeclarations] = useState([] as Bixby.Models.ClientCompanyDeclarationViewModel[]);

  const [isLoadingPorfolio, setIsLoadingPorfolio] = useState(false);
  const [porfolio, setPorfolio] = useState([] as Bixby.Models.DeclarationPortfolio[]);

  const [isLoadingUserOpenRequests, setIsLoadingUserOpenRequests] = useState(false);
  const [userOpenRequests, setUserOpenRequests] = useState([] as IUserCompanyDeclarationView[]);

  const [isLoadingDeclarationHistory, setIsLoadingDeclarationHistory] = useState(false);
  const [declarationHistory, setDeclarationHistory] = useState([] as IHistoricalDeclarationView[]);

  const [userReviewModalData, setUserReviewModalData] = useState<IUserCompanyDeclarationView | undefined>(undefined);
  const [isLoadingReviewApprove, setIsLoadingReviewApprove] = useState(false);

  const [isLoadingReviewReject, setIsLoadingReviewReject] = useState(false);
  const [companyReviewModalData, setCompanyReviewModalData] = useState<DeclarationPortfolio | undefined>(undefined);
  const [declarationReason, setDeclarationReason] = useState('');

  const handleFetchCompanies = useCallback(async () => {
    try {
      setIsLoadingUserOpenRequests(true);
      setUserOpenRequests([]);
      setIsLoadingDeclarationHistory(true);
      setDeclarationHistory([]);
      const results: Bixby.Models.ClientCompanyDeclarationViewModel[] =
        await CompanyService.getDeclarationsForClientApproval();
      if (results) {
        results.forEach(clientCompany => {
          if (!clientCompany.isDeleted) {
            const clientCompanyDeclaration = clientCompany.clientCompanyDeclaration;
            if (clientCompanyDeclaration) {
              //set userOpenRequests with pending user company
              const pendingUsers: IUserCompanyDeclarationView[] = clientCompanyDeclaration.userCompanyDeclarations
                ?.filter(user => user.declarationStatus === UserCompanyDeclarationStatus.Pending)
                ?.map(_ => {
                  return {
                    id: _.id,
                    declarationStatus: _.declarationStatus,
                    requestedBy_Email: _.declarationRequestedBy_Email,
                    requestedDate: _.declarationRequestedDate,
                    companyId: clientCompany.companyId,
                    companyName: clientCompany.companyName,
                    companyDeclarationStatus: clientCompanyDeclaration.declarationStatus,
                    updatedDate: _.updatedDate,
                  };
                });
              setUserOpenRequests(oldArray => [...oldArray, ...pendingUsers]);
            }
          }

          //get and format user history
          const clientCompanyDeclaration = clientCompany.clientCompanyDeclaration;
          if (clientCompanyDeclaration) {
            // add current client declaration
            const currentClientCompanyDeclaration: IHistoricalDeclarationView[] = [
              {
                id: clientCompanyDeclaration.id,
                clientDecisionBy_Email: clientCompanyDeclaration.declarationClientDecisionBy_Email,
                clientDecisionDate: clientCompanyDeclaration.declarationClientDecisionDate,
                declarationStatus: clientCompanyDeclaration.declarationStatus.toString(),
                requestedBy_Email: clientCompanyDeclaration.declarationRequestedBy_Email,
                requestedDate: clientCompanyDeclaration.declarationRequestedDate,
                companyName: clientCompany.companyName,
                companyId: clientCompany.companyId,
                type: 'Company',
                updatedDate: clientCompanyDeclaration.updatedDate,
              },
            ];
            setDeclarationHistory(oldArray => [...oldArray, ...currentClientCompanyDeclaration]);

            // add user declarations
            const currentUserDeclarations: IHistoricalDeclarationView[] =
              clientCompanyDeclaration?.userCompanyDeclarations?.map(_ => {
                return {
                  id: _.id,
                  clientDecisionBy_Email: _.declarationClientDecisionBy_Email,
                  clientDecisionDate: _.declarationClientDecisionDate,
                  declarationStatus: _.declarationStatus.toString(),
                  requestedBy_Email: _.declarationRequestedBy_Email,
                  requestedDate: _.declarationRequestedDate,
                  companyName: clientCompany.companyName,
                  companyId: clientCompany.companyId,
                  type: 'User',
                  updatedDate: _.updatedDate,
                };
              });
            setDeclarationHistory(oldArray => [...oldArray, ...currentUserDeclarations]);

            // add historical user declarations
            clientCompanyDeclaration.userCompanyDeclarations
              ?.filter(
                _ => _.userCompanyDeclarationHistories !== undefined && _.userCompanyDeclarationHistories.length > 0
              )
              ?.forEach(user => {
                const userHistory: IHistoricalDeclarationView[] = user.userCompanyDeclarationHistories.map(_ => {
                  return {
                    id: _.id,
                    clientDecisionBy_Email: _.declarationClientDecisionBy_Email ?? 'N/A',
                    clientDecisionDate: _.declarationClientDecisionDate ?? 'N/A',
                    declarationStatus: _.declarationStatus.toString(),
                    requestedBy_Email: _.declarationRequestedBy_Email,
                    requestedDate: _.declarationRequestedDate,
                    companyName: clientCompany.companyName,
                    companyId: clientCompany.companyId,
                    type: 'User',
                    updatedDate: _.updatedDate,
                  };
                });

                setDeclarationHistory(oldArray => [...oldArray, ...userHistory]);
              });

            // add historical client declaration
            const companyHistory: IHistoricalDeclarationView[] =
              clientCompanyDeclaration?.clientCompanyDeclarationHistory?.map(_ => {
                return {
                  id: _.id,
                  clientDecisionBy_Email: _.declarationClientDecisionBy_Email,
                  clientDecisionDate: _.declarationClientDecisionDate,
                  declarationStatus: _.declarationStatus.toString(),
                  requestedBy_Email: _.declarationRequestedBy_Email,
                  requestedDate: _.declarationRequestedDate,
                  companyName: clientCompany.companyName,
                  companyId: clientCompany.companyId,
                  type: 'Company',
                  updatedDate: _.updatedDate,
                };
              });
            setDeclarationHistory(oldArray => [...oldArray, ...companyHistory]);
            setDeclarationHistory(oldArray => orderBy(oldArray, [_ => _.updatedDate], ['desc']));
          }
        });
      }
      setCompanyDeclarations(results);
    } catch (err) {
      dispatchToastError('Unable to load companies');
    } finally {
      setIsLoadingUserOpenRequests(false);
      setIsLoadingDeclarationHistory(false);
    }
  }, [dispatchToastError]);

  const handleFetchPorfolio = useCallback(async () => {
    try {
      setIsLoadingPorfolio(true);
      const results = await CompanyService.getAllWithDeclarationsForClient();
      const sorted: DeclarationPortfolio[] = Bixby.Utils.orderBy(results, _ => _.privateDeclarationStatus);
      setPorfolio(sorted);
    } catch (err) {
      dispatchToastError('Unable to load portfolio');
    } finally {
      setIsLoadingPorfolio(false);
    }
  }, [dispatchToastError]);

  useEffect(() => {
    handleFetchCompanies();
    handleFetchPorfolio();
  }, [handleFetchCompanies, handleFetchPorfolio]);

  //review user
  const handleReviewUserModalOpen = (modal: IUserCompanyDeclarationView) => {
    setUserReviewModalData(modal);
  };

  const handleUserReviewAction = async (approve: boolean) => {
    if (!userReviewModalData) return;
    try {
      if (approve) {
        setIsLoadingReviewApprove(true);
        await ComplianceService.approveUserDeclarationByClient(userReviewModalData.companyId, userReviewModalData.id);
      } else {
        setIsLoadingReviewReject(true);
        await ComplianceService.rejectUserDeclarationByClient(userReviewModalData.companyId, userReviewModalData.id);
      }
      setUserReviewModalData(undefined);
      await handleFetchCompanies();
      await handleFetchPorfolio();
    } catch (err) {
      setUserReviewModalData(undefined);
      dispatchToastError('Unable to update user request');
    } finally {
      setIsLoadingReviewApprove(false);
      setIsLoadingReviewReject(false);
    }
  };

  //review company
  const handleReviewCompanyModalOpen = (modal: DeclarationPortfolio) => {
    setCompanyReviewModalData(modal);
  };

  const handleReviewCompanyModalClose = () => {
    setCompanyReviewModalData(undefined);
    setDeclarationReason('');
  };

  const handleCompanyReviewAction = async (approve: boolean) => {
    if (!companyReviewModalData) return;
    try {
      if (approve) {
        setIsLoadingReviewApprove(true);
        await ComplianceService.approveDeclarationByClient(companyReviewModalData.companyId, declarationReason);
      } else {
        setIsLoadingReviewReject(true);
        await ComplianceService.rejectDeclarationByClient(companyReviewModalData.companyId, declarationReason);
      }
      setCompanyReviewModalData(undefined);
      await handleFetchCompanies();
      await handleFetchPorfolio();
    } catch (err) {
      setCompanyReviewModalData(undefined);
      dispatchToastError('Unable to update user request');
    } finally {
      setIsLoadingReviewApprove(false);
      setIsLoadingReviewReject(false);
      setDeclarationReason('');
    }
  };

  return (
    <section className="compliance">
      <h2>Portfolio of Companies</h2>
      <div className="grid_layout">
        <div className="compliance_portfolio_grid">
          <div className="grid_container">
            <div className="grid ag-theme-alpine">
              {isLoadingPorfolio ? (
                <Loading />
              ) : (
                <AgGridReact
                  rowData={porfolio}
                  defaultColDef={{
                    flex: 1,
                    minWidth: 100,
                    sortable: true,
                    resizable: true,
                  }}
                  suppressDragLeaveHidesColumns={true}
                  suppressMovableColumns={true}
                  animateRows={true}
                  rowHeight={35}
                  headerHeight={35}
                  columnDefs={[
                    {
                      field: 'companyId',
                      headerName: 'Id',
                      hide: true,
                    },
                    {
                      field: 'loanTicker',
                      headerName: 'Loan Ticker',
                    },
                    {
                      field: 'companyName',
                      headerName: 'Company',
                      cellRenderer: (params: any) => {
                        if (params.data['privateDeclarationStatus'] === ClientCompanyDeclarationStatus.BixbyApproved) {
                          return (
                            <Link className="fake_link" to={`/compliance/${params.data['companyId']}`}>
                              {params.data['companyName']}
                            </Link>
                          );
                        } else {
                          return <div>{params.value}</div>;
                        }
                      },
                    },
                    {
                      field: 'privateDeclarationStatus',
                      headerName: 'Had Private Declaration',
                      cellRenderer: (params: any) => (
                        <DeclarationStatusString declarationStatus={params.data['privateDeclarationStatus']} />
                      ),
                    },
                    {
                      headerName: 'Action',
                      hide: hasBixbyComplianceAutoApprovalFeatureFlag,
                      cellRenderer: (params: any) => {
                        if (params.data['privateDeclarationStatus'] === ClientCompanyDeclarationStatus.Pending) {
                          return (
                            <div
                              className="fake_link"
                              role="button"
                              onClick={() => {
                                handleReviewCompanyModalOpen(params.data);
                              }}>
                              Review
                            </div>
                          );
                        } else {
                          return <div>N/A</div>;
                        }
                      },
                    },
                    {
                      field: 'privateUserCount',
                      headerName: 'Private User Count',
                    },
                    {
                      field: 'totalUserCount',
                      headerName: 'Total User Count',
                    },
                  ]}></AgGridReact>
              )}
            </div>
          </div>
        </div>
      </div>
      <h2>User Open Requests</h2>
      <h3>Total: {userOpenRequests.length}</h3>
      <div className="grid_layout">
        <div className="user_request_grid">
          <div className="grid_container">
            <div className="grid ag-theme-alpine">
              {isLoadingUserOpenRequests ? (
                <Loading />
              ) : (
                <AgGridReact
                  masterDetail={true}
                  detailRowAutoHeight={true}
                  rowData={userOpenRequests}
                  defaultColDef={{
                    flex: 1,
                    minWidth: 100,
                    sortable: true,
                    resizable: true,
                    lockPinned: true,
                  }}
                  rowHeight={32}
                  headerHeight={32}
                  columnDefs={[
                    {
                      field: 'id',
                      headerName: 'Id',
                      hide: true,
                    },
                    {
                      field: 'requestedDate',
                      headerName: 'Last Request',
                      cellRenderer: (params: any) => {
                        return dayjs.utc(params.value).format('MM/DD/YYYY');
                      },
                    },
                    {
                      field: 'requestedBy_Email',
                      headerName: 'Requested By',
                      filter: 'agTextColumnFilter',
                    },
                    {
                      field: 'companyName',
                      headerName: 'Company',
                      cellRenderer: (params: any) => {
                        if (params.data['privateDeclarationStatus'] === ClientCompanyDeclarationStatus.BixbyApproved) {
                          return (
                            <Link className="fake_link" to={`/compliance/${params.data['companyId']}`}>
                              {params.data['companyName']}
                            </Link>
                          );
                        } else {
                          return <div>{params.value}</div>;
                        }
                      },
                    },
                    {
                      headerName: 'Action',
                      hide: hasBixbyComplianceAutoApprovalFeatureFlag,
                      cellRenderer: (params: any) => {
                        if (params.data['companyDeclarationStatus'] === ClientCompanyDeclarationStatus.BixbyApproved) {
                          return (
                            <div
                              className="fake_link"
                              role="button"
                              onClick={() => handleReviewUserModalOpen(params.data)}>
                              Review
                            </div>
                          );
                        } else {
                          return (
                            <DeclarationStatusString declarationStatus={params.data['companyDeclarationStatus']} />
                          );
                        }
                      },
                    },
                  ]}></AgGridReact>
              )}
            </div>
          </div>
        </div>
      </div>
      <h2>All Historical Requests</h2>
      <div className="grid_layout">
        <div className="historical_request_grid">
          <div className="grid_container">
            <div className="grid ag-theme-alpine">
              {isLoadingDeclarationHistory ? (
                <Loading />
              ) : (
                <AgGridReact
                  detailRowAutoHeight={true}
                  rowData={declarationHistory}
                  defaultColDef={{
                    flex: 1,
                    minWidth: 100,
                    sortable: true,
                    resizable: true,
                    lockPinned: true,
                  }}
                  rowHeight={32}
                  headerHeight={32}
                  getRowId={(params: any) => params.data.id}
                  columnDefs={[
                    {
                      field: 'companyId',
                      hide: true,
                    },
                    {
                      field: 'companyName',
                      hide: true,
                      rowGroup: true,
                    },
                    {
                      field: 'requestedDate',
                      headerName: 'Requested Date',
                      cellRenderer: (params: any) => {
                        return params.value
                          ? Bixby.Utils.utcDateAsLocal(params.value).format('MM/DD/YYYY hh:mm:ss')
                          : '';
                      },
                    },
                    {
                      field: 'requestedBy_Email',
                      headerName: 'Requested By',
                    },
                    {
                      field: 'declarationStatus',
                      headerName: 'Status',
                    },
                    {
                      field: 'type',
                      headerName: 'Request Type',
                    },
                    {
                      field: 'clientDecisionDate',
                      headerName: 'Decision Date',
                      cellRenderer: (params: any) => {
                        return params.value ? dayjs.utc(params.value).format('MM/DD/YYYY') : '';
                      },
                    },
                    {
                      field: 'clientDecisionBy_Email',
                      headerName: 'Decision By',
                    },
                  ]}></AgGridReact>
              )}
            </div>
          </div>
        </div>
      </div>
      {userReviewModalData && (
        <Modal
          title="Review User Access"
          active
          onClose={() => {
            setUserReviewModalData(undefined);
          }}>
          <>
            <p>
              Please approve or reject access for {userReviewModalData.requestedBy_Email} to view{' '}
              {userReviewModalData.companyName}.
            </p>
            <div className="button_side_wrap">
              <BixButton
                type="button"
                onClick={() => handleUserReviewAction(true)}
                useLoading
                isLoading={isLoadingReviewApprove}
                disabled={isLoadingReviewApprove || isLoadingReviewReject}>
                Approve
              </BixButton>
              <BixButton
                light
                type="button"
                onClick={() => handleUserReviewAction(false)}
                useLoading
                isLoading={isLoadingReviewReject}
                disabled={isLoadingReviewApprove || isLoadingReviewReject}>
                Reject
              </BixButton>
            </div>
          </>
        </Modal>
      )}
      {companyReviewModalData && (
        <Modal title="Review Company Access" active onClose={handleReviewCompanyModalClose}>
          <>
            <p>
              Please approve or reject creation of a private company for{' '}
              <span className="font-bold">{companyReviewModalData.companyName}</span>.
            </p>
            <div className="input_wrap">
              <label className="label" htmlFor="declarationReason">
                Declaration Reason
              </label>
              <textarea
                id="declarationReason"
                placeholder="Enter reason (optional)"
                value={declarationReason}
                onChange={e => setDeclarationReason(e.target.value)}
                className="input"
              />
            </div>
            <div className="button_side_wrap">
              <BixButton
                type="button"
                onClick={() => handleCompanyReviewAction(true)}
                useLoading
                isLoading={isLoadingReviewApprove}
                disabled={isLoadingReviewApprove || isLoadingReviewReject}>
                Accept
              </BixButton>
              <BixButton
                light
                type="button"
                onClick={() => handleCompanyReviewAction(false)}
                isLoading={isLoadingReviewReject}
                disabled={isLoadingReviewReject || isLoadingReviewApprove}>
                Reject
              </BixButton>
            </div>
          </>
        </Modal>
      )}
    </section>
  );
};

const mapStateToProps = (state: AppState) => {
  const { hasBixbyComplianceAutoApprovalFeatureFlag } = getHasBixbyComplianceAutoApprovalFeatureFlagState(state);
  return {
    hasBixbyComplianceAutoApprovalFeatureFlag,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchToastError: (message: string) => dispatch(toastError(message)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CompanyAccess);
